import { useCallback, useEffect, useRef, useState } from "react";
import { useAudioPlayer } from "react-use-audio-player";
import { Config } from "services/configuration/eatSmartConfig";
import { type Maybe, TransactionState, TransactionType } from "types";
import { networkTrace, shouldTraceNetworkDebug } from "utils/debug/networkTrace";
import { useCountdown } from "./timers";

// ── TEMP audio diagnostic ───────────────────────────────────────────────────────────────────────
// On the kiosk (Hexnode Browser Lite, no Web Inspector) the open-door WARNING shows but no SOUND
// plays, even with html5:true. To find which layer eats the audio, mirror the Howler global state
// into the on-screen NetworkDebugOverlay (gated by ?networkDebug=true). Reads `window.Howler` defensively
// — it carries no type. Remove this helper and the [audio] networkTrace() calls below together with
// the rest of the network-debug scaffolding (see networkTrace.ts).
const howlerState = (): string => {
	const howler = (
		window as unknown as {
			Howler?: { usingWebAudio?: boolean; ctx?: { state?: string }; _muted?: boolean; volume?: () => number };
		}
	).Howler;
	if (!howler) {
		return "Howler=none";
	}
	return `usingWebAudio=${howler.usingWebAudio} ctx=${howler.ctx?.state ?? "none"} muted=${howler._muted} vol=${howler.volume?.()}`;
};

export const useOpenDoorAlarm = (orderType?: Maybe<TransactionType>, transactionState?: Maybe<TransactionState>) => {
	const { alarms } = Config;
	const { displayWarningTimeout, firstAlarm, secondAlarm, thirdAlarm, totalAlarmTimeout, refillWarningTimeout } =
		alarms;
	const warningDelay = totalAlarmTimeout;
	const playSoundParamFromUrl = new URLSearchParams(window.location.search).get("playSound");

	const isRefill = orderType === TransactionType.Refill;
	// When order type is refill, we delay the initial warning by 300s (5 minutes)
	// we have to set the delay to 400 so the first warning is triggered when countdown reaches 100s
	const initialTimer = isRefill ? refillWarningTimeout : warningDelay;
	const { countdown, resetCountdown, startCountdown } = useCountdown(initialTimer);
	const [displayOpenedDoorWarning, setDisplayOpenedDoorWarning] = useState<boolean>(false);

	// `alarm1`/`alarm2`/`alarm3` capture the full player so the TEMP diagnostic below can read `.error`;
	// the destructured load/play/stop names are unchanged.
	const alarm1 = useAudioPlayer();
	const alarm2 = useAudioPlayer();
	const alarm3 = useAudioPlayer();
	const { load: alarm1Load, play: alarm1Play, stop: alarm1Stop } = alarm1;
	const { load: alarm2Load, play: alarm2Play, stop: alarm2Stop } = alarm2;
	const { load: alarm3Load, play: alarm3Play, stop: alarm3Stop } = alarm3;

	const resetAlarmState = useCallback(() => {
		alarm1Stop();
		alarm2Stop();
		alarm3Stop();
		setDisplayOpenedDoorWarning(false);
	}, [alarm1Stop, alarm2Stop, alarm3Stop]);

	const handleResetCountdown = () => {
		resetCountdown(true);
		setDisplayOpenedDoorWarning(false);
		resetAlarmState();
	};

	useEffect(() => {
		// `html5: true` is load-bearing for TWO reasons — do not revert it to plain Web Audio:
		//   1. Audio: it plays each alarm through an HTML5 <audio> element. The default (Web Audio API)
		//      leaves the AudioContext suspended until a real touch/click unlocks it — but on these kiosks
		//      the user interacts via a physical RFID card + door and often never taps the screen, so the
		//      context is never unlocked and .play() is silently dropped (the warning still shows because
		//      it's pure React state). The <audio> element plays without a gesture under the kiosk
		//      WKWebView media setting, so the alarm is actually audible.
		//   2. Memory: it streams instead of decoding the whole file into a Web Audio AudioBuffer. The
		//      buffers were ~20 MB each (uncompressed PCM), loaded when a refill/order reaches step 3 —
		//      ~100 MB of RAM spiking at refill time, a major contributor to the iOS WKWebView memory-kill
		//      (blank screen). Streaming is fine for a door-open nag alarm.
		// TEMP: when ?networkDebug=true, attach onload/onplay tracers so the kiosk overlay shows whether
		// each <audio> element actually loads and whether Howler ever reports playback starting.
		const traceAudio = shouldTraceNetworkDebug();
		const opts = (label: string) =>
			traceAudio
				? {
						loop: true,
						html5: true,
						onload: () => networkTrace(`[audio] ${label} loaded`),
						onplay: () => networkTrace(`[audio] ${label} onplay; ${howlerState()}`),
					}
				: { loop: true, html5: true };
		alarm1Load(firstAlarm.sound, opts("alarm1"));
		alarm2Load(secondAlarm.sound, opts("alarm2"));
		alarm3Load(thirdAlarm.sound, opts("alarm3"));
		if (traceAudio) {
			networkTrace(`[audio] mounted 3 alarms (html5); ${howlerState()}`);
		}
		startCountdown(true);
		return () => {
			resetAlarmState();
			startCountdown(false);
		};
	}, [
		alarm1Load,
		alarm2Load,
		alarm3Load,
		firstAlarm.sound,
		resetAlarmState,
		secondAlarm.sound,
		startCountdown,
		thirdAlarm.sound,
	]);

	// The open-door warning + alarm only apply while the transaction is ACTIVE (the door is open
	// mid-transaction). The instant it leaves Active — StockCheck or Finished — fully stop: silence the
	// alarms, hide the warning, AND stop the countdown. Stopping (not restarting) is the key difference
	// from the manual "I'm still here" confirm: a restarted countdown would re-reach the warning
	// threshold and re-trigger while the transaction lingers (e.g. a long refill sitting at StockCheck),
	// which is why the refill warning would not go away on its own. A transaction that disappears
	// entirely (state undefined) is intentionally NOT handled here — OrderStep3 unmounts with it and the
	// mount effect's cleanup stops everything.
	useEffect(() => {
		if (transactionState != null && transactionState !== TransactionState.Active) {
			resetAlarmState();
			startCountdown(false);
		}
	}, [transactionState, resetAlarmState, startCountdown]);

	useEffect(() => {
		if (countdown === displayWarningTimeout) {
			setDisplayOpenedDoorWarning(true);
		} else if (playSoundParamFromUrl && playSoundParamFromUrl === "true") {
			if (countdown === firstAlarm.timeout) {
				if (shouldTraceNetworkDebug()) {
					networkTrace(`[audio] play() alarm1 @${countdown}; ${howlerState()}`);
				}
				alarm1Play();
			} else if (countdown === secondAlarm.timeout) {
				if (shouldTraceNetworkDebug()) {
					networkTrace(`[audio] play() alarm2 @${countdown}; ${howlerState()}`);
				}
				alarm1Stop();
				alarm2Play();
			} else if (countdown === thirdAlarm.timeout) {
				if (shouldTraceNetworkDebug()) {
					networkTrace(`[audio] play() alarm3 @${countdown}; ${howlerState()}`);
				}
				alarm2Stop();
				alarm3Play();
			}
		}
	}, [
		countdown,
		alarm1Play,
		alarm1Stop,
		alarm2Play,
		alarm2Stop,
		alarm3Play,
		displayWarningTimeout,
		firstAlarm.timeout,
		playSoundParamFromUrl,
		secondAlarm.timeout,
		thirdAlarm.timeout,
	]);

	// TEMP: surface a load/play error from any alarm on the kiosk overlay (a failed mp3 fetch or a
	// gesture-blocked <audio> element shows up here). Gated by ?networkDebug=true. Remove with the rest.
	useEffect(() => {
		if (!shouldTraceNetworkDebug()) {
			return;
		}
		if (alarm1.error) {
			networkTrace(`[audio] alarm1 ERROR: ${alarm1.error}`);
		}
		if (alarm2.error) {
			networkTrace(`[audio] alarm2 ERROR: ${alarm2.error}`);
		}
		if (alarm3.error) {
			networkTrace(`[audio] alarm3 ERROR: ${alarm3.error}`);
		}
	}, [alarm1.error, alarm2.error, alarm3.error]);

	const handleResetCountdownRef = useRef(handleResetCountdown);
	handleResetCountdownRef.current = handleResetCountdown;

	const stableResetCountdown = useCallback(() => handleResetCountdownRef.current(), []);

	return {
		resetCountdown: stableResetCountdown,
		displayOpenedDoorWarning: displayOpenedDoorWarning,
	};
};
